import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ChatRoot } from "@/components/chat/ChatRoot";
import { getChatWithMessages } from "@/lib/chat/getChatWithMessages";
import { Loader2 } from "lucide-react";
import { NotesEditorProvider } from "@/context/NotesEditorContext";
import { ChatShareDialog } from "@/components/chat/ChatShareDialog";
import { ContentItem } from "@/types/content";

const ChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const [chatData, setChatData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [chat, setChat] = useState<ContentItem | null>(null);

  useEffect(() => {
    if (!chatId) {
      setLoading(false);
      return;
    }

    getChatWithMessages(chatId)
      .then((data) => {
        setChatData(data);
        // Map the content to ContentItem format
        if (data.content) {
          setChat({
            id: data.content.id,
            title: data.content.title,
            type: data.content.type as ContentItem["type"],
            thumbnailUrl: data.content.thumbnail_url,
            sourceUrl: data.content.source_url,
            storagePath: data.content.storage_path,
            roomId: data.content.room_id,
            createdAt: data.content.created_at,
            updatedAt: data.content.updated_at,
            visibility: data.content.visibility as "private" | "public" | undefined,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading chat:", error);
        setLoading(false);
      });
  }, [chatId]);

  const handleVisibilityChange = async (chatId: string, visibility: "private" | "public") => {
    setChat((prev) => prev ? { ...prev, visibility } : null);
  };

  if (loading) {
    return (
      <DashboardShell chat={chat} onShareChat={() => setIsShareDialogOpen(true)}>
        <main className="flex-1 px-4 md:px-6">
          <div className="-mx-4 md:mx-0">
            <div className="flex h-[calc(100vh-70px)] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
        </main>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell chat={chat} onShareChat={() => setIsShareDialogOpen(true)}>
      <main className="flex-1 px-4 md:px-6">
        <div className="-mx-4 md:mx-0">
          <div className="overflow-hidden bg-background md:h-[calc(100vh-70px)]">
            <NotesEditorProvider>
              <ChatRoot 
                initialMessages={chatData?.messages || []} 
                chatId={chatId}
              />
            </NotesEditorProvider>
          </div>
        </div>
      </main>

      {/* Chat Share Dialog */}
      {chat && (
        <ChatShareDialog
          chat={chat}
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          onVisibilityChange={handleVisibilityChange}
        />
      )}
    </DashboardShell>
  );
};

export default ChatPage;
